import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, X } from 'lucide-react';
import { addBackofficeOrganizationMember } from '../api';
import type {
  AddOrganizationMemberInput,
  AddOrganizationMemberResponse,
  OrganizationMemberInput,
} from '../types';
import { OrganizationMemberEditor } from './OrganizationMemberEditor';

interface AddOrganizationMemberModalProps {
  organizationId: string;
  organizationName: string;
  onClose: () => void;
  onAdded: (response: AddOrganizationMemberResponse) => void;
}

function toAddOrganizationMemberInput(
  member: OrganizationMemberInput
): AddOrganizationMemberInput | null {
  if (member.role === 'owner') {
    return null;
  }

  return {
    kind: member.kind,
    email: member.email,
    role: member.role,
    delivery: member.delivery,
    ...(member.userId ? { userId: member.userId } : {}),
    ...(member.name !== undefined ? { name: member.name } : {}),
  };
}

export function AddOrganizationMemberModal({
  organizationId,
  organizationName,
  onClose,
  onAdded,
}: AddOrganizationMemberModalProps) {
  const [members, setMembers] = useState<OrganizationMemberInput[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  async function handleSubmit() {
    if (submitting) return;
    if (members.length !== 1) {
      setError('Select one member to add to the organization.');
      return;
    }

    const input = toAddOrganizationMemberInput(members[0]);
    if (!input) {
      setError('Owner transfers are not supported here.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await addBackofficeOrganizationMember(organizationId, input);
      onAdded(response);
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : 'Failed to add organization member');
    } finally {
      setSubmitting(false);
    }
  }

  const modal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-organization-member-title"
        className="max-h-[92vh] w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-2xl"
      >
        <div className="border-b border-neutral-200 px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2
                id="add-organization-member-title"
                className="font-inter-tight text-xl font-semibold text-neutral-900"
              >
                Add member
              </h2>
              <p className="mt-1 text-small text-neutral-500">{organizationName}</p>
            </div>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              className="rounded-md p-2 text-neutral-500 hover:bg-backoffice-50 hover:text-neutral-900"
              aria-label="Close"
            >
              <X className="size-5" />
            </button>
          </div>
        </div>

        <div className="max-h-[calc(92vh-150px)] overflow-y-auto px-6 py-5">
          {error ? (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-small text-red-800">
              <AlertCircle className="mt-0.5 size-4 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          <OrganizationMemberEditor
            organizationId={organizationId}
            members={members}
            onMembersChange={setMembers}
            allowedRoles={['admin', 'member']}
            maxMembers={1}
            searchLabel="Search an existing user or enter an email to invite"
          />
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-neutral-200 px-6 py-4">
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={submitting || members.length !== 1}
            className="rounded-lg bg-backoffice-500 px-4 py-2 text-small font-semibold text-white hover:bg-backoffice-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Adding member' : 'Add member'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
